import { useCallback, useEffect, useState } from 'react'

import { getSyncState, onSyncState, type SyncState } from '../lib/sync'

/**
 * Чтение из локального снимка.
 *
 * Экраны не обращаются к сети напрямую: они читают то, что уже лежит на
 * устройстве, и перечитывают, когда синхронизация обновила снимок. Поэтому
 * поведение в подвале и в офисе одинаковое — меняется только свежесть данных.
 */
export function useLocal<T>(load: () => Promise<T[]>, deps: unknown[] = []): {
  rows: T[]
  loading: boolean
  reload: () => void
} {
  const [rows, setRows] = useState<T[]>([])
  const [loading, setLoading] = useState(true)
  const [tick, setTick] = useState(0)

  const reload = useCallback(() => setTick((n) => n + 1), [])

  useEffect(() => {
    let alive = true
    load()
      .then((r) => alive && setRows(r))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, ...deps])

  // Данные поменялись — перечитываем. Следим за счётчиком изменений, а не за
  // всем состоянием: иначе экран перерисовывался бы на каждый признак связи.
  useEffect(() => {
    let last = getSyncState().rev
    return onSyncState((s) => {
      if (s.rev !== last) {
        last = s.rev
        reload()
      }
    })
  }, [reload])

  return { rows, loading, reload }
}

/** Состояние связи и очереди — для полоски «нет сети» и счётчика неотправленного. */
export function useSync(): SyncState {
  const [s, setS] = useState<SyncState>(getSyncState())
  useEffect(() => onSyncState(setS), [])
  return s
}

/** Секунда для идущего таймера. Тикает локально, сервер не дёргается. */
export function useTicker(active: boolean): void {
  const [, force] = useState(0)
  useEffect(() => {
    if (!active) return
    const id = setInterval(() => force((n) => n + 1), 1000)
    return () => clearInterval(id)
  }, [active])
}
